import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";
import { PageShell, PageHeader } from "../components/PageShell";
import BeamProgress from "../components/BeamProgress";
import PageBackdrop from "../components/PageBackdrop";

const ease = [0.16, 1, 0.3, 1];

// The approved portrait (unchanged)
const PORTRAIT_URL = "https://customer-assets-cm19k8pv.emergentagent.net/job_project-intelligence-3/artifacts/tn2dvh8f_WhatsApp%20Image%202026-07-23%20at%2015.10.47.jpeg";

/* ─── About narrative — 4 languages ─── */
const INTRO_BODY = {
    en: [
        "Long before my work moved across academia, diplomacy, international cooperation and project development, I was exposed to something that would profoundly shape the way I see the world: the power of social action.",
        "From an early age, my father introduced me to communities where resources were scarce, opportunities were limited, and sometimes it seemed that there was very little with which to begin. Yet those spaces taught me an important lesson:",
        "the absence of resources does not necessarily mean the absence of possibilities.", // pull #1
        "I witnessed how projects — large, medium or even very small — could positively influence the lives of entire communities. Not only by delivering tangible results, but by changing people's perception of what was possible, creating opportunities, strengthening collective effort and opening a path towards better conditions.",
        "Over time, I began to understand that meaningful change does not happen simply because there is a good intention.",
        "It requires direction, structure, organization and clear goals.", // pull #2
        "When people, ideas and resources are properly brought together, actions can create real impact — and successful experiences can become models for addressing other challenges and changing other contexts.",
        "That understanding gradually became part of the way I approached my own path.",
        "My work eventually moved across academia, diplomacy, international cooperation and project development. These experiences may appear different, but they share a common question:",
        "How do ideas become action?", // pull #3 — central question
        "Life is a Project grew from that intersection — where experience shapes ideas, research meets practice, and a vision begins to take form through projects.",
        "It is an independent platform for ideas, research and projects — a growing body of work shaped by experience and guided by a belief that meaningful change begins when ideas are given direction, structure and the will to act.",
    ],
    es: [
        "Mucho antes de que mi trabajo se moviera entre la academia, la diplomacia, la cooperación internacional y el desarrollo de proyectos, me expuse a algo que marcaría profundamente mi manera de mirar el mundo: la fuerza de la acción social.",
        "Desde muy joven, mi padre me llevó a comunidades donde los recursos escaseaban, las oportunidades eran limitadas y a veces parecía que había muy poco con qué empezar. Pero esos lugares me enseñaron una lección importante:",
        "la ausencia de recursos no significa necesariamente la ausencia de posibilidades.",
        "Vi cómo proyectos — grandes, medianos o incluso muy pequeños — podían influir positivamente en la vida de comunidades enteras. No sólo por sus resultados tangibles, sino porque cambiaban la percepción de lo posible, creaban oportunidades, fortalecían el esfuerzo colectivo y abrían un camino hacia mejores condiciones.",
        "Con el tiempo empecé a entender que el cambio significativo no ocurre por buena intención.",
        "Requiere dirección, estructura, organización y objetivos claros.",
        "Cuando personas, ideas y recursos se articulan bien, la acción puede crear impacto real — y las experiencias exitosas pueden convertirse en modelos para enfrentar otros retos y transformar otros contextos.",
        "Ese entendimiento se fue haciendo parte del modo en que enfrenté mi propio camino.",
        "Con el tiempo mi trabajo se movió entre la academia, la diplomacia, la cooperación internacional y el desarrollo de proyectos. Son experiencias distintas, pero comparten una misma pregunta:",
        "¿Cómo se convierten las ideas en acción?",
        "Life is a Project nació en ese cruce — donde la experiencia da forma a las ideas, la investigación encuentra la práctica y una visión empieza a tomar cuerpo a través de los proyectos.",
        "Es una plataforma independiente para ideas, investigación y proyectos — una obra en construcción, moldeada por la experiencia y guiada por la convicción de que el cambio significativo empieza cuando a las ideas se les da dirección, estructura y voluntad de acción.",
    ],
    nl: [
        "Lang voordat mijn werk zich bewoog tussen academie, diplomatie, internationale samenwerking en projectontwikkeling, werd ik geraakt door iets dat mijn manier van kijken naar de wereld diepgaand zou vormen: de kracht van sociaal handelen.",
        "Van jongs af aan nam mijn vader me mee naar gemeenschappen waar middelen schaars waren, kansen beperkt en waar het soms leek alsof er heel weinig was om mee te beginnen. Toch leerden die plekken me een belangrijke les:",
        "de afwezigheid van middelen betekent niet noodzakelijk de afwezigheid van mogelijkheden.",
        "Ik zag hoe projecten — grote, middelgrote en zelfs heel kleine — het leven van hele gemeenschappen ten goede konden veranderen. Niet alleen door tastbare resultaten, maar door de perceptie van wat mogelijk was te verschuiven, kansen te scheppen, collectieve inspanning te versterken en een weg te openen naar betere omstandigheden.",
        "Met de tijd begon ik te begrijpen dat betekenisvolle verandering niet ontstaat uit goede bedoelingen alleen.",
        "Ze vraagt richting, structuur, organisatie en heldere doelen.",
        "Wanneer mensen, ideeën en middelen goed samenkomen, kan actie werkelijke impact hebben — en geslaagde ervaringen kunnen modellen worden om andere uitdagingen aan te pakken en andere contexten te veranderen.",
        "Dat begrip werd langzaam deel van de manier waarop ik mijn eigen weg benaderde.",
        "Mijn werk bewoog uiteindelijk tussen academie, diplomatie, internationale samenwerking en projectontwikkeling. Deze ervaringen lijken verschillend, maar delen dezelfde vraag:",
        "Hoe worden ideeën actie?",
        "Life is a Project ontstond op dat kruispunt — waar ervaring ideeën vormt, onderzoek en praktijk elkaar raken, en waar een visie via projecten vorm krijgt.",
        "Het is een onafhankelijk platform voor ideeën, onderzoek en projecten — een groeiend oeuvre, gevormd door ervaring en geleid door de overtuiging dat betekenisvolle verandering begint wanneer ideeën richting, structuur en handelingsbereidheid krijgen.",
    ],
    fr: [
        "Bien avant que mon travail ne traverse l’université, la diplomatie, la coopération internationale et le développement de projets, j’ai été exposé à ce qui allait façonner en profondeur ma manière de voir le monde : la force de l’action sociale.",
        "Très tôt, mon père m’a fait connaître des communautés où les ressources manquaient, les opportunités étaient limitées et où il semblait parfois qu’il n’y avait presque rien pour commencer. Ces lieux m’ont pourtant appris une leçon importante :",
        "l’absence de ressources ne signifie pas nécessairement l’absence de possibilités.",
        "J’ai vu comment des projets — grands, moyens ou même très petits — pouvaient influencer positivement la vie de communautés entières. Non seulement par leurs résultats tangibles, mais en modifiant la perception du possible, en créant des opportunités, en renforçant l’effort collectif et en ouvrant un chemin vers de meilleures conditions.",
        "Avec le temps, j’ai compris que le changement significatif ne naît pas d’une bonne intention.",
        "Il exige direction, structure, organisation et objectifs clairs.",
        "Quand les personnes, les idées et les ressources s’articulent bien, l’action peut produire un impact réel — et les expériences réussies deviennent des modèles pour d’autres défis et d’autres contextes.",
        "Cette compréhension est peu à peu entrée dans la manière dont j’ai abordé mon propre chemin.",
        "Mon travail a fini par traverser l’université, la diplomatie, la coopération internationale et le développement de projets. Ces expériences paraissent différentes, mais elles partagent une même question :",
        "Comment les idées deviennent-elles action ?",
        "Life is a Project est né à cette intersection — là où l’expérience façonne les idées, où la recherche rencontre la pratique, et où une vision commence à prendre forme à travers des projets.",
        "C’est une plateforme indépendante pour idées, recherche et projets — une œuvre qui grandit, façonnée par l’expérience et guidée par la conviction que le changement significatif commence lorsque les idées reçoivent direction, structure et volonté d’agir.",
    ],
};

// The indices in INTRO_BODY that get emphasised typographic treatment.
const PULL_INDICES = new Set([2, 5]);           // "absence of resources…", "requires direction…"
const CENTRAL_QUESTION_INDEX = 9;               // "How do ideas become action?"

/* ─── Pepe section (unchanged, 2 paragraphs) ─── */
const PEPE_BODY_SIMPLE = {
    en: [
        "Pepe Castro Salas devoted his life to community development, social initiatives and public service in the Dominican Republic. He believed in practical action: ideas mattered when they could survive their encounter with reality and improve people's lives.",
        "His philosophy was simple but demanding: a project is where a vision meets the ground. Life is a Project carries that idea forward, developing and adapting it for a changing world — through new languages, new tools and new fields of practice.",
    ],
    es: [
        "Pepe Castro Salas dedicó su vida al desarrollo comunitario, a iniciativas sociales y al servicio público en la República Dominicana. Creía en la acción práctica: las ideas importan cuando sobreviven a su encuentro con la realidad y mejoran la vida de las personas.",
        "Su filosofía era sencilla pero exigente: un proyecto es donde una visión toca el suelo. Life is a Project retoma esa idea, la desarrolla y la adapta para un mundo que cambia — con nuevos idiomas, nuevas herramientas y nuevos campos de práctica.",
    ],
    nl: [
        "Pepe Castro Salas wijdde zijn leven aan gemeenschapsontwikkeling, sociale initiatieven en publieke dienst in de Dominicaanse Republiek. Hij geloofde in praktische actie: ideeën tellen wanneer ze de ontmoeting met de werkelijkheid overleven en het leven van mensen verbeteren.",
        "Zijn filosofie was eenvoudig maar veeleisend: een project is waar een visie de grond raakt. Life is a Project draagt dat idee verder, ontwikkelt en past het aan een veranderende wereld aan — in nieuwe talen, nieuwe instrumenten en nieuwe praktijkgebieden.",
    ],
    fr: [
        "Pepe Castro Salas a consacré sa vie au développement communautaire, aux initiatives sociales et au service public en République dominicaine. Il croyait à l’action pratique : les idées comptent quand elles survivent à leur rencontre avec la réalité et améliorent la vie des gens.",
        "Sa philosophie était simple mais exigeante : un projet, c’est là où une vision touche le sol. Life is a Project prolonge cette idée, la développe et l’adapte à un monde qui change — en de nouvelles langues, de nouveaux outils et de nouveaux champs de pratique.",
    ],
};

const LABELS = {
    origin:        { en: "The Origin",              es: "El Origen",              nl: "De Oorsprong",           fr: "L’Origine" },
    ideaContinues: { en: "The Idea That Continues", es: "La Idea que Continúa",   nl: "Het Idee Dat Voortduurt",fr: "L’Idée qui Continue" },
};

export default function AboutPage() {
    const { lang } = useLanguage();
    const intro = pick3(INTRO_BODY, lang);
    const cols = pick3(t3.about.columns, lang);
    const pepe = pick3(PEPE_BODY_SIMPLE, lang);

    return (
        <PageShell testId="page-about" backdrop={<PageBackdrop variant="about" />}>
            <BeamProgress />
            <PageHeader
                overline={pick3(t3.about.overline, lang)}
                title={pick3(t3.about.title, lang)}
            />

            {/* ── Two-column editorial block: portrait LEFT, narrative RIGHT ── */}
            <section aria-label="Introduction" className="mb-20 md:mb-24">
                <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
                    <div className="col-span-12 md:col-span-5 md:sticky md:top-8">
                        <motion.figure
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease }}
                            className="portrait-frame aspect-[3/4] max-w-[420px]"
                            data-testid="about-portrait"
                        >
                            <img
                                src={PORTRAIT_URL}
                                alt="Portrait"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover duotone-warm"
                                style={{ objectPosition: "50% 22%" }}
                            />
                        </motion.figure>
                    </div>
                    <div className="col-span-12 md:col-span-7 max-w-[62ch]">
                        <div className="space-y-5">
                            {intro.map((p, i) => {
                                const isPull = PULL_INDICES.has(i);
                                const isCentral = i === CENTRAL_QUESTION_INDEX;
                                const commonMotion = {
                                    initial: { opacity: 0, y: 10 },
                                    whileInView: { opacity: 1, y: 0 },
                                    viewport: { once: true, margin: "-6%" },
                                    transition: { duration: 0.75, ease, delay: (i % 5) * 0.04 },
                                };
                                if (isCentral) {
                                    return (
                                        <motion.p
                                            key={i}
                                            data-testid="about-central-question"
                                            {...commonMotion}
                                            className="font-display italic text-[color:var(--navy)] leading-[1.15] my-4"
                                            style={{ fontSize: "clamp(30px, 4.2vw, 52px)" }}
                                        >
                                            {p}
                                        </motion.p>
                                    );
                                }
                                if (isPull) {
                                    return (
                                        <motion.p
                                            key={i}
                                            data-testid={`about-pull-${i}`}
                                            {...commonMotion}
                                            className="border-l-2 border-[color:var(--gold)] pl-5 md:pl-6 font-display italic text-[22px] md:text-[26px] leading-[1.28] text-[color:var(--navy)] max-w-[46ch]"
                                        >
                                            {p}
                                        </motion.p>
                                    );
                                }
                                return (
                                    <motion.p
                                        key={i}
                                        {...commonMotion}
                                        className="text-[17px] md:text-[18px] leading-[1.72] text-[color:var(--navy)]/85"
                                    >
                                        {p}
                                    </motion.p>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Compact metadata row — Academic · Practice · Origin ── */}
            <section aria-label="Background" className="mb-24 border-t border-[color:var(--line-strong)] pt-8">
                <div className="grid grid-cols-1 md:grid-cols-3">
                    {cols.map((c, i) => (
                        <div
                            key={c.k}
                            data-testid={`about-meta-${i}`}
                            className={`py-4 md:py-5 md:pr-6 ${i > 0 ? "md:pl-6 md:border-l border-[color:var(--line)]" : ""}`}
                        >
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-2">
                                {c.k}
                            </div>
                            <div className="font-display text-[18px] md:text-[19px] leading-[1.3] text-[color:var(--navy)]">
                                {c.v}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── The Origin — Pepe Castro Salas ── */}
            <section id="pepe" aria-labelledby="pepe-heading" className="mb-24 border-t border-[color:var(--line)] pt-14">
                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-6 flex items-center gap-4">
                    <span aria-hidden className="gold-thin" />
                    <span>{pick3(LABELS.origin, lang)}</span>
                </div>

                <h2
                    id="pepe-heading"
                    className="font-display font-light tracking-[-0.01em] leading-[1.05] text-[color:var(--navy)] mb-6 max-w-[24ch]"
                    style={{ fontSize: "clamp(30px, 3.8vw, 48px)" }}
                >
                    {pick3(t3.about.pepeHeading, lang)}
                </h2>

                <p className="font-display italic text-[22px] md:text-[26px] leading-[1.3] text-[color:var(--navy)]/90 max-w-[42ch] mb-12">
                    {pick3(t3.about.pepeLede, lang)}
                </p>

                <blockquote className="relative border-l-2 border-[color:var(--gold)] pl-6 md:pl-10 my-12 max-w-[54ch]">
                    <p className="font-display italic text-[26px] md:text-[34px] leading-[1.22] text-[color:var(--navy)]">
                        “{pick3(t3.about.pepeQuote, lang)}”
                    </p>
                    <footer className="mt-6 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        {pick3(t3.about.pepeAttribution, lang)}
                    </footer>
                </blockquote>

                <div className="max-w-[62ch] space-y-5">
                    {pepe.map((p, i) => (
                        <p key={i} className="text-[16px] md:text-[17px] leading-[1.72] text-[color:var(--navy)]/85">
                            {p}
                        </p>
                    ))}
                </div>
            </section>

            {/* ── The Idea That Continues — founding statement close ── */}
            <section
                id="origin"
                aria-labelledby="founding-heading"
                className="mt-8 border-t border-[color:var(--line)] pt-16"
            >
                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-10 flex items-center gap-4">
                    <span aria-hidden className="gold-thin" />
                    <span id="founding-heading">{pick3(LABELS.ideaContinues, lang)}</span>
                </div>
                <blockquote className="max-w-[52ch]">
                    <p className="font-display italic text-[22px] md:text-[30px] leading-[1.28] tracking-[-0.005em] text-[color:var(--navy)]">
                        “{pick3(t3.about.originQuote, lang)}”
                    </p>
                    <footer className="mt-8 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        {pick3(t3.about.originBy, lang)}
                    </footer>
                </blockquote>
            </section>
        </PageShell>
    );
}
